import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  Bell,
  Layers,
  Sparkles,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { HOME_COMPARISON } from "@/lib/route-content";

/*
 * "The old way vs. the Sidestep way": the five tools the old way needs sit
 * beside the one Sidestep workspace, with lines flowing from each tool into
 * it, above paired before/after rows. Nothing animates away, so the old way
 * stays readable; reduced motion renders the same picture without motion.
 */

// Copy lives in route-content.ts so the prerendered crawler HTML reads the
// same rows; the icons are paired here by position.
const PAIR_ICONS: LucideIcon[] = [BarChart3, Layers, Sparkles, Bell, Users];
const PAIRS = HOME_COMPARISON.map((row, i) => ({ ...row, icon: PAIR_ICONS[i] }));

/* ── The "five tabs" the old way needs ── */

interface ToolWindow {
  title: string;
  badge: string;
  accent: string;
  icon?: string;
}

const TOOLS: ToolWindow[] = [
  { title: "Ads Manager", badge: "Account 1 of 12", accent: "#0866FF", icon: "/meta-icon.png" },
  { title: "Google Ads", badge: "Switch account", accent: "#F2A900", icon: "/google-ads-icon-64.png" },
  { title: "client-report-FINAL-v3.xlsx", badge: "Export CSV", accent: "#1E8E3E" },
  { title: "ChatGPT", badge: "Paste your data…", accent: "#10A37F" },
  { title: "Notes", badge: "Where's the brief?", accent: "#9AA4B2" },
];

/*
 * Stage layouts in their own coordinate space; everything (window positions,
 * connector paths) scales together with the stage width, so nothing drifts.
 * Nothing leaves the stage: the old way stays readable for as long as the
 * visitor looks at it, and the lines show the tools flowing into Sidestep.
 */
interface Layout {
  w: number;
  h: number;
  toolW: number;
  /** window centres and tilt */
  tools: { x: number; y: number; r: number }[];
  /** where each connector leaves its window */
  from: (i: number) => [number, number];
  /** where each connector enters the Sidestep window */
  to: (i: number) => [number, number];
  /** connector shape */
  path: (a: [number, number], b: [number, number]) => string;
  side: { x: number; y: number; w: number };
  labels: { old: [number, number]; next: [number, number] };
}

const WIDE: Layout = {
  w: 960,
  h: 460,
  toolW: 200,
  tools: [
    { x: 120, y: 120, r: -5 },
    { x: 348, y: 105, r: 4 },
    { x: 112, y: 262, r: 3 },
    { x: 338, y: 250, r: -4 },
    { x: 232, y: 388, r: -2 },
  ],
  from(i) {
    const t = this.tools[i];
    return [t.x + 100, t.y];
  },
  to: (i) => [578, 172 + i * 30],
  path: ([ax, ay], [bx, by]) => `M${ax},${ay} C${ax + 90},${ay} ${bx - 90},${by} ${bx},${by}`,
  side: { x: 755, y: 240, w: 360 },
  labels: { old: [232, 14], next: [755, 52] },
};

/** Narrowest stage that shows WIDE at ≥ ~0.83 scale (badge text ≥ 9px). */
const WIDE_MIN = 800;

const NARROW: Layout = {
  w: 360,
  h: 820,
  toolW: 166,
  tools: [
    { x: 87, y: 100, r: -4 },
    { x: 274, y: 116, r: 3 },
    { x: 86, y: 245, r: 3 },
    { x: 274, y: 260, r: -3 },
    { x: 180, y: 388, r: -2 },
  ],
  from(i) {
    const t = this.tools[i];
    return [t.x, t.y + 48];
  },
  to: (i) => [120 + i * 30, 518],
  path: ([ax, ay], [bx, by]) => `M${ax},${ay} C${ax},${ay + 70} ${bx},${by - 70} ${bx},${by}`,
  side: { x: 180, y: 668, w: 340 },
  labels: { old: [180, 14], next: [180, 470] },
};

function useStageWidth(ref: React.RefObject<HTMLDivElement>) {
  const [width, setWidth] = useState(WIDE.w);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

function ToolCard({ tool }: { tool: ToolWindow }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[hsl(224_32%_15%)] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6)] overflow-hidden">
      <div className="flex items-center gap-1.5 border-b border-white/[0.06] px-3 py-2">
        <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
        <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
        <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
        {tool.icon ? (
          <img src={tool.icon} alt="" className="ml-2 h-3 w-3" />
        ) : (
          <span className="ml-2 h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: tool.accent }} />
        )}
        <span className="truncate text-[10px] sm:text-[11px] font-medium text-white/70">{tool.title}</span>
      </div>
      <div className="space-y-1.5 p-3">
        <div className="h-1.5 w-4/5 rounded bg-white/10" />
        <div className="h-1.5 w-3/5 rounded bg-white/[0.07]" />
        <div className="pt-1">
          <span className="inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full bg-destructive/15 px-2 py-0.5 text-[9.5px] sm:text-[11px] font-semibold text-red-300">
            <X className="h-2.5 w-2.5 shrink-0" strokeWidth={3} />
            <span className="truncate">{tool.badge}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function SidestepCard() {
  return (
    <div className="glow-frame rounded-2xl bg-[hsl(224_36%_13%)] shadow-[0_24px_60px_-16px_hsl(275_43%_43%/0.45)]">
      <div className="flex items-center gap-2 border-b border-white/[0.06] px-4 py-2.5">
        <img src="/logo-128.webp" alt="" className="h-4 w-4" />
        <span className="text-xs font-semibold text-white/85">Sidestep</span>
        <span className="ml-auto rounded-full bg-secondary/15 px-2 py-0.5 text-[10px] font-semibold text-secondary">
          One workspace
        </span>
      </div>
      <ul className="space-y-1.5 p-3 sm:p-4">
        {PAIRS.map(({ short, icon: Icon }, i) => (
          <li key={short} className="flex items-center gap-2.5 rounded-lg bg-white/[0.035] px-2.5 py-2">
            <span
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
              style={{ background: `hsl(${193 + i * 22} 55% 50% / 0.16)` }}
            >
              <Icon className="h-3.5 w-3.5" style={{ color: `hsl(${193 + i * 22} 60% 66%)` }} />
            </span>
            <span className="text-[12.5px] sm:text-[13px] font-medium text-white/85">{short}</span>
            <span className="ml-auto h-1.5 w-1.5 rounded-full bg-secondary" />
          </li>
        ))}
      </ul>
    </div>
  );
}

function StageLabel({ at, accent, children }: { at: [number, number]; accent?: boolean; children: ReactNode }) {
  return (
    <span
      className={
        "absolute -translate-x-1/2 whitespace-nowrap rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider " +
        // solid fill so the connector lines pass behind the label, not through it
        (accent ? "border-secondary/30 bg-[hsl(204_45%_12%)] text-secondary" : "border-white/10 bg-[hsl(224_36%_11%)] text-white/55")
      }
      style={{ left: at[0], top: at[1] }}
    >
      {children}
    </span>
  );
}

function BeforeAfterStage() {
  const stageRef = useRef<HTMLDivElement>(null);
  const width = useStageWidth(stageRef);
  // The wide layout only when it fits at a readable scale; below that the
  // stacked one, which may grow a little on tablets.
  const L = width < WIDE_MIN ? NARROW : WIDE;
  const k = Math.min(L === NARROW ? 1.25 : 1, width / L.w);
  const reduce = !!useReducedMotion();
  // Trigger on position, not on a visible fraction: the stacked stage can be
  // taller than a short landscape viewport, where a ratio would never be met.
  const inView = useInView(stageRef, { once: true, margin: "0px 0px -25% 0px" });
  const shown = reduce || inView;
  return (
    // Decorative: the paired rows below carry the same content as text.
    <div ref={stageRef} aria-hidden="true" className="mx-auto w-full max-w-[960px]">
      {/* The layout is drawn at its native size and scaled as one unit, so
          positions, cards, text and connectors always shrink together. */}
      <div className="mx-auto" style={{ width: L.w * k, height: L.h * k }}>
      <div className="relative origin-top-left" style={{ width: L.w, height: L.h, transform: `scale(${k})` }}>
      {/* Connectors: draw in, then keep a slow flow toward Sidestep */}
      <svg viewBox={`0 0 ${L.w} ${L.h}`} className="absolute inset-0 h-full w-full overflow-visible" aria-hidden="true">
        <defs>
          <linearGradient id="ow-flow" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="hsl(193 60% 58%)" />
            <stop offset="1" stopColor="hsl(282 45% 60%)" />
          </linearGradient>
        </defs>
        {TOOLS.map((_, i) => {
          const d = L.path(L.from(i), L.to(i));
          return (
            <g key={i}>
              <motion.path
                d={d}
                fill="none"
                stroke="hsl(220 30% 30%)"
                strokeWidth={1.5}
                initial={{ pathLength: reduce ? 1 : 0 }}
                animate={{ pathLength: shown ? 1 : 0 }}
                transition={{ duration: 0.8, delay: 0.5 + i * 0.08, ease: "easeOut" }}
              />
              {!reduce && shown && (
                <path d={d} fill="none" stroke="url(#ow-flow)" strokeWidth={1.8} strokeDasharray="6 70" strokeLinecap="round" opacity={0.9}>
                  <animate attributeName="stroke-dashoffset" from="76" to="0" dur={`${1.6 + i * 0.15}s`} begin={`${1.3 + i * 0.1}s`} repeatCount="indefinite" />
                </path>
              )}
            </g>
          );
        })}
      </svg>

      <StageLabel at={L.labels.old}>The old way · 5 tools</StageLabel>
      {TOOLS.map((tool, i) => {
        const t = L.tools[i];
        return (
          <motion.div
            key={tool.title}
            className="absolute"
            style={{ left: t.x - L.toolW / 2, top: t.y, width: L.toolW, y: "-50%", rotate: t.r }}
            initial={reduce ? false : { opacity: 0, scale: 0.94 }}
            animate={shown ? { opacity: 1, scale: 1 } : undefined}
            transition={{ duration: 0.45, delay: i * 0.08, ease: "easeOut" }}
          >
            {/* gentle bob so the mess feels alive without moving out of reach */}
            <motion.div
              animate={reduce ? undefined : { y: [0, -3, 0] }}
              transition={{ duration: 4 + i * 0.7, repeat: Infinity, ease: "easeInOut" }}
            >
              <ToolCard tool={tool} />
            </motion.div>
          </motion.div>
        );
      })}

      <StageLabel at={L.labels.next} accent>The Sidestep way · 1 workspace</StageLabel>
      <motion.div
        className="absolute"
        style={{ left: L.side.x - L.side.w / 2, top: L.side.y, width: L.side.w, y: "-50%" }}
        initial={reduce ? false : { opacity: 0, scale: 0.94 }}
        animate={shown ? { opacity: 1, scale: 1 } : undefined}
        transition={{ duration: 0.6, delay: 1.2, ease: "easeOut" }}
      >
        <SidestepCard />
      </motion.div>
      </div>
      </div>
    </div>
  );
}

function PairedRows() {
  return (
    <div className="relative mx-auto mt-14 sm:mt-20 max-w-5xl">
      {/* Highlight behind the Sidestep column (desktop) */}
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[calc(50%-1.75rem)] rounded-[22px] border border-secondary/25 bg-gradient-to-b from-secondary/[0.07] to-primary/[0.05] md:block" />
      {/* Visual column headers; each row carries its own screen-reader labels. */}
      <div aria-hidden="true" className="relative hidden md:grid grid-cols-[1fr_3.5rem_1fr] items-center px-0 pb-2 pt-6">
        <div className="pl-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">The old way</div>
        <div />
        <div className="pl-7 text-xs font-semibold uppercase tracking-wider text-secondary">With Sidestep</div>
      </div>
      <ul className="relative space-y-3 md:space-y-0">
        {PAIRS.map(({ old, next, icon: Icon }, i) => (
          <li
            key={old}
            className="grid grid-cols-1 md:grid-cols-[1fr_3.5rem_1fr] items-center gap-2 md:gap-0 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 md:rounded-none md:border-0 md:border-t md:border-white/[0.06] md:bg-transparent md:p-0 md:first:border-t-0"
          >
            <div className="flex items-start gap-2.5 md:py-5 md:pl-2 md:pr-4">
              <X aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-destructive/60" />
              <span className="text-[13px] sm:text-[15px] text-muted-foreground/80 leading-snug">
                <span className="sr-only">The old way: </span>
                {old}
              </span>
            </div>
            <div className="hidden md:flex justify-center">
              <ArrowRight aria-hidden="true" className="h-4 w-4 text-white/25" />
            </div>
            <div className="flex items-start gap-2.5 md:py-5 md:pl-7 md:pr-6">
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
                style={{ background: `hsl(${193 + i * 22} 55% 50% / 0.16)` }}
              >
                <Icon className="h-3.5 w-3.5" style={{ color: `hsl(${193 + i * 22} 60% 66%)` }} />
              </span>
              <span className="text-[14px] sm:text-[15px] font-semibold text-foreground leading-snug">
                <span className="sr-only">With Sidestep: </span>
                {next}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function OldWayVsSidestep() {
  return (
    <>
      <BeforeAfterStage />
      <PairedRows />
    </>
  );
}

export default OldWayVsSidestep;
